import english from "./locales/en.json" with { type: "json" };

export type UiLanguage = "el" | "en";
export const UI_LANGUAGE_KEY = "easyorders.pwa.uiLanguage";
const translations: Record<string, string> = english;
const listeners = new Set<() => void>();
let accountKey: string | null = null;

function read(key: string): string | null {
  try { return typeof localStorage === "undefined" ? null : localStorage.getItem(key); }
  catch { return null; }
}

function write(key: string, value: string): void {
  try { if (typeof localStorage !== "undefined") localStorage.setItem(key, value); }
  catch { /* Language remains usable when the browser disables persistent storage. */ }
}

function isLanguage(value: unknown): value is UiLanguage {
  return value === "el" || value === "en";
}

let language: UiLanguage = read(UI_LANGUAGE_KEY) === "en" ? "en" : "el";

export function getLanguage(): UiLanguage { return language; }
export function getLocale(): string { return language === "en" ? "en-GB" : "el-GR"; }

export function subscribeLanguage(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function setLanguage(next: UiLanguage): void {
  if (!isLanguage(next)) return;
  write(UI_LANGUAGE_KEY, next);
  if (accountKey) write(accountKey, next);
  if (typeof document !== "undefined") document.documentElement.lang = next;
  if (next !== language) {
    language = next;
    listeners.forEach(listener => listener());
  }
}

/** Account selection is local immediately; the existing preferences API supplies cross-device sync. */
export function selectLanguageAccount(session: { businessSlug: string; user: { id: number } } | null): void {
  accountKey = session ? `easyorders.pwa.${encodeURIComponent(session.businessSlug)}.user.${session.user.id}.uiLanguage` : null;
  const saved = accountKey ? read(accountKey) : read(UI_LANGUAGE_KEY);
  setLanguage(isLanguage(saved) ? saved : language);
}

/** Translate UI copy only. Values inserted into placeholders are never translated. */
export function t(source: string, ...values: unknown[]): string {
  const key = source.trim();
  const translated = language === "en" ? translations[key] ?? key : key;
  const text = translated.replace(/\{(\d+)\}/g, (match, index: string) =>
    Number(index) < values.length ? String(values[Number(index)] ?? "") : match);
  return source.slice(0, source.length - source.trimStart().length) + text + source.slice(source.trimEnd().length);
}

const patterns = Object.entries(translations)
  .filter(([source]) => /\{\d+\}/.test(source))
  .map(([el, en]) => ({ el, en }));

function matchMessage(template: string, message: string): string[] | null {
  const keys: number[] = [];
  const escaped = template.split(/(\{\d+\})/).map(part => {
    const token = /^\{(\d+)\}$/.exec(part);
    if (token) { keys.push(Number(token[1])); return "([\\s\\S]*?)"; }
    return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }).join("");
  const match = new RegExp(`^${escaped}$`).exec(message);
  if (!match) return null;
  const values: string[] = [];
  keys.forEach((key, index) => { values[key] = match[index + 1]; });
  return values;
}

/** Known service messages can arrive in Greek; custom server/printer prose is left intact. */
export function translateMessage(message: string): string {
  if (translations[message]) return t(message);
  for (const [el, en] of Object.entries(translations)) {
    if (message === en) return language === "el" ? el : en;
  }
  for (const pattern of patterns) {
    const values = matchMessage(pattern.el, message) ?? matchMessage(pattern.en, message);
    if (values) return t(pattern.el, ...values);
  }
  return message;
}

export function orderStatusLabel(status: string): string {
  const labels: Record<string, string> = { "ΑΝΟΙΧΤΗ": "Open", "ΤΥΠΩΘΗΚΕ": "Printed", "ΚΛΕΙΣΤΗ": "Closed", "ΑΚΥΡΩΜΕΝΗ": "Cancelled" };
  return language === "en" ? labels[status.toUpperCase()] ?? status : status;
}

export function orderEventLabel(event: string): string {
  const labels: Record<string, string> = {
    "ΑΡΧΙΚΗ ΠΑΡΑΓΓΕΛΙΑ": "Initial order", "ΠΑΡΑΓΓΕΛΙΑ": "Order",
    "ΣΥΜΠΛΗΡΩΜΑΤΙΚΗ ΠΑΡΑΓΓΕΛΙΑ": "Additional order", "ΣΥΜΠΛ. ΠΑΡΑΓΓΕΛΙΑ": "Additional order",
    "ΑΚΥΡΩΣΗ ΠΑΡΑΓΓΕΛΙΑΣ": "Order cancellation", "ΑΚΥΡΩΣΗ ΠΑΡΑΓΓ.": "Order cancellation",
    "ΔΙΟΡΘΩΣΗ ΠΑΡΑΓΓΕΛΙΑΣ": "Order correction", "ΔΙΟΡΘ. ΠΑΡΑΓΓΕΛΙΑΣ": "Order correction",
    "ΕΠΑΝΕΚΤΥΠΩΣΗ": "Reprint", "ΑΛΛΑΓΗ ΤΡΑΠΕΖΙΟΥ": "Table change"
  };
  return language === "en" ? labels[event.toUpperCase()] ?? event : event;
}
